import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { DepositMethod } from 'data/payments/models';
import { inrPaymentLanguages, pixPaymentLanguages } from 'locales/i18n';
import { safeLs } from 'utils/localStorage';

// Separate from the app-wide 'i18n' key so other apps keep their language
const LANGUAGES_BY_METHOD: Partial<
  Record<DepositMethod, { languages: string[]; storageKey: string }>
> = {
  INR: { languages: inrPaymentLanguages, storageKey: 'i18n_inr' },
  PIX: { languages: pixPaymentLanguages, storageKey: 'i18n_pix' },
};

// INR and PIX payers get the page in their local language by default or in their previously chosen language
export const usePaymentLanguages = (method: DepositMethod | null) => {
  const { i18n } = useTranslation();
  const config = method ? LANGUAGES_BY_METHOD[method] ?? null : null;

  useEffect(() => {
    if (!config) {
      if (method) {
        if (i18n.language === 'pt')
          i18n.changeLanguage(safeLs?.getItem?.('i18n') ?? 'ru');
        return;
      }
      const isPtPhone =
        typeof navigator !== 'undefined' &&
        navigator.language?.toLowerCase().startsWith('pt');
      if (isPtPhone && i18n.language !== 'pt') i18n.changeLanguage('pt');
      return;
    }
    const stored = safeLs?.getItem?.(config.storageKey);
    const language =
      stored && config.languages.includes(stored)
        ? stored
        : config.languages[0];
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [config, method, i18n]);

  return config;
};
